import { defineMessages } from "react-intl";
import { buffers, Channel, channel, eventChannel, EventChannel, Task } from "redux-saga";
import {
  actionChannel,
  call,
  cancel,
  delay,
  flush,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { getType } from "typesafe-actions";
import { v4 as uuidv4 } from "uuid";

import ChatLocalMessageType from "@mapmycustomers/shared/enum/chat/ChatLocalMessageType";
import ChatVoiceError from "@mapmycustomers/shared/enum/chat/ChatVoiceError";
import ChatVoiceHint from "@mapmycustomers/shared/enum/chat/ChatVoiceHint";
import ChatVoicePhase from "@mapmycustomers/shared/enum/chat/ChatVoicePhase";
import ChatMessage from "@mapmycustomers/shared/types/chat/ChatMessage";
import { FieldAgentTranscribeResponse } from "@mapmycustomers/shared/types/chat/FieldAgentTranscribe";
import Organization from "@mapmycustomers/shared/types/Organization";
import { isApiError, isNotEmptyString } from "@mapmycustomers/shared/util/assert";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { fieldAgentChatAnalytics } from "@app/util/analytic/fieldAgentAnalytics";
import { ChatThreadKey } from "@app/util/chat/getChatThreadKey";
import createVoiceCapture, { VoiceCapture } from "@app/util/chat/voice/createVoiceCapture";
import {
  setCurrentVoiceCapture,
  takePendingAudioContext,
} from "@app/util/chat/voice/currentVoiceCapture";
import debugDownloadClip, { debugLogClipResult } from "@app/util/chat/voice/debugDownloadClip";
import loggingService from "@app/util/logging";

import { appendLocalChatMessage, clearChatConversation } from "../conversation/actions";
import { getChatThreadConversationId } from "../conversation/selectors";

import {
  cancelVoiceRecording,
  enterVoiceReview,
  finishVoiceRecording,
  pauseVoiceRecording,
  resumeVoiceRecording,
  sendVoiceMessage,
  setVoiceError,
  setVoiceHint,
  setVoicePart,
  startVoiceRecording,
} from "./actions";
import { getChatVoiceElapsedMs, getChatVoiceParts, getChatVoicePhase } from "./selectors";

// The worker count IS the concurrency cap. Three is a safety valve rather than a routine
// constraint: clips arrive every few seconds and each upload takes one or two, so real
// concurrency sits at one. It exists for a fast talker on a truck's uplink, where six parallel
// uploads starve each other and the LAST clip lands later than if they had gone one at a time.
export const VOICE_TRANSCRIBE_CONCURRENCY = 3;

// An IDLE deadline, not a total one: it is re-armed on every part that lands, so a slow but
// advancing drain is never killed. `timeout: 0` on the upload means the network will never end a
// stalled clip, so this is the only thing that can end a stuck "Transcribing…". Generous, because
// ending early loses words the rep already said.
export const DRAIN_IDLE_TIMEOUT_MS = 120_000;

const messages = defineMessages({
  blocked: {
    id: "chat.voice.notice.blocked",
    defaultMessage: "Microphone is blocked. Allow it in your browser settings to use voice.",
    description: "Local chat notice when the browser has blocked microphone access",
  },
  empty: {
    id: "chat.voice.notice.empty",
    defaultMessage: "Didn't catch that — please try again.",
    description: "Local chat notice when a recording produced no transcript at all",
  },
  noDevice: {
    id: "chat.voice.notice.noDevice",
    defaultMessage: "No microphone found.",
    description: "Local chat notice when no microphone input device is available",
  },
  unavailable: {
    id: "chat.voice.notice.unavailable",
    defaultMessage: "Voice input isn't available right now.",
    description: "Local chat notice when the microphone could not be opened for an unknown reason",
  },
});

interface VoiceClip {
  blob: Blob;
  seq: number;
}

interface VoiceErrorEvent {
  error: ChatVoiceError;
  type: "error";
}

// The capture's three callbacks, forwarded as one stream so the session has a single place to
// read them from.
type VoiceCaptureEvent =
  | { blob: Blob; seq: number; type: "clip" }
  | { hint: ChatVoiceHint; type: "hint" }
  | VoiceErrorEvent;

interface VoiceSession {
  capture: VoiceCapture;
  events: EventChannel<VoiceCaptureEvent>;
}

const isVoiceErrorEvent = (event: VoiceCaptureEvent): event is VoiceErrorEvent =>
  event.type === "error";

const createVoiceCaptureChannel = (audioContext: AudioContext): VoiceSession => {
  // eventChannel invokes its subscriber synchronously, so this is assigned before the call below
  // returns and long before anything can read it.
  let capture!: VoiceCapture;

  const events = eventChannel<VoiceCaptureEvent>(
    (emit) => {
      capture = createVoiceCapture({
        audioContext,
        onClip: (seq, blob) => emit({ blob, seq, type: "clip" }),
        onError: (error) => emit({ error, type: "error" }),
        onHintChange: (hint) => emit({ hint, type: "hint" }),
      });
      setCurrentVoiceCapture(capture);
      // Closing the channel and releasing the microphone are one operation, so they cannot drift
      // apart: this unsubscribe is the only thing that ends the session's hold on the device.
      return () => {
        capture.close();
        setCurrentVoiceCapture(undefined);
      };
    },
    // Buffered, unlike eventChannel's buffers.none() default: the session is not taking from this
    // channel while it awaits stop(), and an emit with no taker and no buffer is DROPPED — which
    // is precisely the rep's last sentence.
    buffers.expanding()
  );

  return { capture, events };
};

function* appendVoiceNotice(
  localType: ChatLocalMessageType,
  text: string,
  threadKey: ChatThreadKey
) {
  const id: ChatMessage["id"] = `l-${uuidv4()}`;
  // The key is passed in rather than selected here: a notice belongs to the thread the rep started
  // the recording on, and by the time one lands the whole recording and its transcription have
  // round-tripped, so a rep who opened another record meanwhile would be handed it on that one.
  yield put(
    appendLocalChatMessage({ id, createdAt: new Date().toISOString(), localType, text, threadKey })
  );
}

const voiceUnavailableText = (reason: ChatVoiceError): string => {
  switch (reason) {
    case ChatVoiceError.BLOCKED:
      return i18nService.formatMessage(
        messages.blocked,
        "Microphone is blocked. Allow it in your browser settings to use voice."
      );
    case ChatVoiceError.NO_DEVICE:
      return i18nService.formatMessage(messages.noDevice, "No microphone found.");
    default:
      return i18nService.formatMessage(
        messages.unavailable,
        "Voice input isn't available right now."
      );
  }
};

// A microphone that never opened does not deserve a full-window takeover to announce that nothing
// happened. A local message also persists in the transcript, so a rep who clicks three times sees
// the reason three times rather than watching a toast flash past.
export function* onVoiceUnavailable(reason: ChatVoiceError, threadKey: ChatThreadKey) {
  const text = voiceUnavailableText(reason);
  yield call(appendVoiceNotice, ChatLocalMessageType.VOICE_UNAVAILABLE, text, threadKey);
}

export function* onTranscribeClip(seq: number, blob: Blob) {
  // No-ops unless REACT_APP_DEBUG_VOICE_RECORDING is on.
  yield call(debugDownloadClip, seq, blob);
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: FieldAgentTranscribeResponse = yield callApi(
      "transcribeFieldAgentAudio",
      orgId,
      blob
    );
    yield call(debugLogClipResult, seq, response.transcript);
    yield put(setVoicePart({ seq, text: response.transcript }));
  } catch (error) {
    // Never rethrows. A 409 means the clip held no speech and is not an error at all — the
    // opposite of 409 on /chat, which getChatFailureType maps to BUSY, so that helper must not be
    // reused here. Everything else fills the slot too, so one dead clip cannot strand the settled
    // prefix behind a hole that never fills.
    if (!isApiError(error) || error.status !== 409) {
      loggingService.error("Voice clip transcription failed", error);
      // Toastless — the empty slot and the eventual "Didn't catch that" notice are the rep-facing
      // surface. handleError still runs for its 401 → login() re-auth path; nothing else has one.
      yield put(handleError({ displayNotification: false, error }));
    }
    yield call(debugLogClipResult, seq, "");
    yield put(setVoicePart({ seq, text: "" }));
  }
}

function* transcribeWorker(clips: Channel<VoiceClip>) {
  while (true) {
    const { blob, seq }: VoiceClip = yield take(clips);
    // Belt-and-braces: onTranscribeClip already swallows everything, and a throw escaping here
    // would end this generator, silently shrinking the pool to N-1 for the rest of the session
    // with nothing anywhere saying so.
    try {
      yield call(onTranscribeClip, seq, blob);
    } catch (error) {
      loggingService.error("Voice transcribe worker failed", error);
    }
  }
}

// Waits for every slot from 0 to clipCount - 1 to fill. Two invariants make it terminate: seq is
// assigned when and only when a clip is emitted, and every slot resolves — failures fill with "".
export function* drain(clipCount: number) {
  if (clipCount === 0) {
    // Done pressed with nothing recorded: vacuously all-empty.
    return;
  }
  while (true) {
    const parts: Record<number, string> = yield select(getChatVoiceParts);
    const filled = Object.keys(parts).length;
    if (filled >= clipCount) {
      return;
    }
    const { timeout } = yield race({
      part: take(setVoicePart),
      timeout: delay(DRAIN_IDLE_TIMEOUT_MS),
    });
    if (timeout) {
      loggingService.warning("Voice transcription drain timed out", { clipCount, filled });
      return;
    }
  }
}

function* watchCaptureEvents(events: EventChannel<VoiceCaptureEvent>, clips: Channel<VoiceClip>) {
  while (true) {
    const event: VoiceCaptureEvent = yield take(events);
    if (event.type === "clip") {
      yield put(clips, { blob: event.blob, seq: event.seq });
    } else if (event.type === "hint") {
      yield put(setVoiceHint({ hint: event.hint }));
    } else {
      yield put(setVoiceError({ error: event.error }));
      // Treated as Done rather than as a failure: the rep keeps the words they already said,
      // where every other branch throws them away. Gated on the phase because the capture can
      // also fail while the drain is already running, and re-finishing would drag review back to
      // TRANSCRIBING.
      const phase: ChatVoicePhase = yield select(getChatVoicePhase);
      if (phase === ChatVoicePhase.PAUSED || phase === ChatVoicePhase.RECORDING) {
        yield put(finishVoiceRecording({ at: performance.now() }));
      }
    }
  }
}

// The saga is the capture's only owner, so the Pause and Resume buttons — which dispatch actions
// and nothing else — reach the microphone here. Without this a paused recording keeps the mic
// open and keeps cutting clips while the timecode stands still.
//
// One at a time, because pause() awaits a `dataavailable` round trip and a Resume that overtook it
// would open a recorder the pause's queued flush then stops — leaving the capture nominally
// recording with no recorder until the next silence cut, and the rep's words in between gone.
function* watchCaptureControls(capture: VoiceCapture) {
  // An actionChannel, not a bare take: a bare take only sees actions dispatched while the
  // generator is suspended on it, so a Resume arriving while we were still awaiting that flush
  // would be DROPPED — Redux back in RECORDING, the timecode climbing, the capture still paused
  // and cutting nothing, and no later event able to repair it. The channel buffers instead
  // (buffers.expanding() by default), so serializing costs no actions.
  const controls: Channel<{ type: string }> = yield actionChannel([
    pauseVoiceRecording,
    resumeVoiceRecording,
  ]);
  try {
    while (true) {
      const { type }: { type: string } = yield take(controls);
      // These call straight into MediaRecorder — resume() constructs one synchronously, pause()
      // returns the flush chain. An escaping throw would abort this fork, and with it
      // chatVoiceSagas and every sibling in chatSaga's all([...]), taking the whole conversation
      // down for the rest of the page session over one failed device call.
      try {
        if (type === getType(pauseVoiceRecording)) {
          yield call([capture, capture.pause]);
        } else {
          yield call([capture, capture.resume]);
        }
      } catch (error) {
        loggingService.error("Voice capture control failed", error);
      }
    }
  } finally {
    // An actionChannel keeps a taker registered on the store's channel until it is closed, so
    // without this every session would leave one behind, buffering pause/resume actions into a
    // channel nobody reads for the rest of the page session.
    controls.close();
  }
}

function* startCapture({ capture, events }: VoiceSession, threadKey: ChatThreadKey) {
  try {
    yield call([capture, capture.start]);
    return true;
  } catch (error) {
    // start() calls onError(reason) and then rethrows, so the mapped reason is already sitting in
    // the channel's buffer. Reading it back keeps the DOMException-to-ChatVoiceError mapping in
    // the one place that owns it.
    const buffered: VoiceCaptureEvent[] = yield flush(events);
    const reason = buffered.find(isVoiceErrorEvent)?.error ?? ChatVoiceError.UNKNOWN;
    if (reason === ChatVoiceError.UNKNOWN) {
      // A refused permission or a missing device is the rep's own machine answering, not a
      // defect. Anything else is ours to look at.
      loggingService.error("Voice capture failed to start", error);
    }
    yield call(onVoiceUnavailable, reason, threadKey);
    fieldAgentChatAnalytics.failed(["Voice Recording"], { reason });
    // The click already moved the phase to RECORDING, so the overlay has to be taken back down
    // explicitly. Last statement on this path, because onStartVoiceRecording takes this same
    // action and cancels the session the moment it lands.
    yield put(cancelVoiceRecording());
    return false;
  }
}

function* finishVoiceSession(capture: VoiceCapture, threadKey: ChatThreadKey) {
  // stop() resolves with the TOTAL clips emitted, once the final `dataavailable` has fired. One
  // short would mean dropping the rep's last sentence, which is where the instruction usually is.
  let clipCount = 0;
  try {
    clipCount = yield call([capture, capture.stop]);
  } catch (error) {
    // A stop that rejects leaves no count to drain against and nothing more will arrive, so carry
    // on with whatever already landed. Propagating instead would abort chatVoiceSagas and every
    // sibling in chatSaga's all([...]) — the rep would be left with a dead chat instead of a
    // notice, over a session that was ending anyway.
    loggingService.error("Voice capture failed to stop", error);
  }
  yield call(drain, clipCount);
  const parts: Record<number, string> = yield select(getChatVoiceParts);
  const durationMs: number = yield select(getChatVoiceElapsedMs);
  const transcribed = Object.values(parts).some(isNotEmptyString);
  fieldAgentChatAnalytics.completed(["Voice Recording"], {
    durationMs,
    partCount: Object.keys(parts).length,
    transcribed,
  });
  if (transcribed) {
    yield put(enterVoiceReview());
    return;
  }
  // Every clip came back empty. Review would show the rep an empty field and ask them to send it,
  // so skip it, go back to the chat, and say what happened.
  yield call(
    appendVoiceNotice,
    ChatLocalMessageType.VOICE_EMPTY,
    i18nService.formatMessage(messages.empty, "Didn't catch that — please try again."),
    threadKey
  );
  yield put(cancelVoiceRecording());
}

const closeVoiceSession = (
  events: EventChannel<VoiceCaptureEvent>,
  clips: Channel<VoiceClip>
): void => {
  // Closing the event channel runs its unsubscribe, which releases the microphone and clears the
  // registry slot.
  events.close();
  // Ends the workers, which are otherwise parked on a take that can never resolve.
  clips.close();
};

function* runVoiceSession(audioContext: AudioContext, threadKey: ChatThreadKey) {
  // buffers.expanding() by default, so a clip waits for a free worker rather than being dropped.
  const clips: Channel<VoiceClip> = yield call(channel);
  // The last effect before the try, and a synchronous one: from the moment it returns the capture
  // owns the AudioContext, and the finally below is what closes both it and the microphone.
  const session: VoiceSession = yield call(createVoiceCaptureChannel, audioContext);

  try {
    const started: boolean = yield call(startCapture, session, threadKey);
    if (!started) {
      return;
    }
    for (let worker = 0; worker < VOICE_TRANSCRIBE_CONCURRENCY; worker += 1) {
      yield fork(transcribeWorker, clips);
    }
    yield fork(watchCaptureEvents, session.events, clips);
    yield fork(watchCaptureControls, session.capture);
    // getUserMedia's await IS the permission prompt, and the page stays interactive behind it, so
    // every footer button can be pressed before this session was in a position to hear it. Read
    // the phase the store actually holds rather than assuming we are still RECORDING: waiting on
    // a Done that has already been and gone would strand the rep on "Transcribing…" with only a
    // Cancel button.
    let phase: ChatVoicePhase = yield select(getChatVoicePhase);
    if (phase === ChatVoicePhase.PAUSED) {
      try {
        yield call([session.capture, session.capture.pause]);
      } catch (error) {
        loggingService.error("Voice capture failed to pause", error);
      }
      // Re-read, because that pause awaited a `dataavailable` round trip and a Done landing in
      // the middle of it would be missed by the take below — the same strand this reconciliation
      // exists to close, in a smaller window.
      phase = yield select(getChatVoicePhase);
    }
    if (phase === ChatVoicePhase.PAUSED || phase === ChatVoicePhase.RECORDING) {
      // Done, or a capture that died mid-session — watchCaptureEvents turns the second into the
      // first so both leave by one path.
      yield take(finishVoiceRecording);
    }
    yield call(finishVoiceSession, session.capture, threadKey);
  } finally {
    // Unconditional, NOT `if (yield cancelled())`. On Done → drain → review → send this task
    // completes normally, so a cancelled() guard would skip teardown on the single most common
    // path, leaving the tracks live and the browser's recording indicator lit — once per
    // recording, cumulatively, until the AudioContext cap turns the feature off outright.
    yield call(closeVoiceSession, session.events, clips);
  }
}

function* onStartVoiceRecording({ payload }: ReturnType<typeof startVoiceRecording>) {
  // Taken as this handler's very first effect, before any yield that cancellation could land on,
  // so a context the click handler created is never left open with no owner. Nothing else closes
  // an untaken one, and the browser caps how many a page may hold.
  const audioContext: AudioContext | undefined = yield call(takePendingAudioContext);
  if (!audioContext) {
    // The click handler is the only producer of both the context and this action, so the two
    // arriving apart is a defect. Put the overlay back down rather than leaving a dead recorder.
    loggingService.error("Voice recording started with no audio context");
    yield put(cancelVoiceRecording());
    return;
  }
  const conversationId: number | undefined = yield select(
    getChatThreadConversationId,
    payload.threadKey
  );
  fieldAgentChatAnalytics.initiated(["Voice Recording"], { conversationId });
  // Carried on the action: every notice this session appends belongs to the thread the rep
  // pressed the microphone on, however long the recording runs.
  const session: Task = yield fork(runVoiceSession, audioContext, payload.threadKey);
  yield take([cancelVoiceRecording, sendVoiceMessage.request]);
  yield cancel(session);
}

export function* chatVoiceSagas() {
  // takeLatest, not takeEvery: this is what guarantees a second recording cancels the first, so
  // the single capture slot can never have two owners.
  yield takeLatest(startVoiceRecording, onStartVoiceRecording);
  // Signing out unmounts ChatWidget, but this saga would keep running and keep uploading clips
  // under the previous user's org id. clearChatConversation is the same user-switch signal
  // store/app/sagas.ts already dispatches.
  yield takeEvery(clearChatConversation, function* () {
    yield put(cancelVoiceRecording());
  });
}
